import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  lazy,
  Suspense,
} from 'react';
import useSWR from 'swr';
import { clsx } from 'clsx';
import { AdminSidebar } from './AdminSidebar';
// Code splitting：子頁改為 lazy 載入，切到對應 Tab 時再載入 chunk
const AdminUsers = lazy(() =>
  import('./AdminUsers').then(m => ({ default: m.AdminUsers }))
);
const AdminProducts = lazy(() =>
  import('./AdminProducts').then(m => ({ default: m.AdminProducts }))
);
// Coupon feature hidden - 優惠券功能已隱藏 (2024-11-11) - May be restored in the future
const AdminOrders = lazy(() =>
  import('./AdminOrders').then(m => ({ default: m.AdminOrders }))
);
const AdminPreorders = lazy(() =>
  import('./AdminPreorders').then(m => ({ default: m.AdminPreorders }))
);
const AdminShipments = lazy(() =>
  import('./AdminShipments').then(m => ({ default: m.AdminShipments }))
);
const AdminSurveyDesign = lazy(() =>
  import('./AdminSurveyDesign').then(m => ({ default: m.AdminSurveyDesign }))
);
const AdminRagChatbot = lazy(() =>
  import('./AdminRagChatbot').then(m => ({ default: m.AdminRagChatbot }))
);
const AdminAppointments = lazy(() =>
  import('./AdminAppointments').then(m => ({ default: m.AdminAppointments }))
);
const AdminMarketing = lazy(() =>
  import('./AdminDiscounts').then(m => ({ default: m.AdminMarketing }))
);
const AdminAvailabilitySettings = lazy(() =>
  import('./AdminAvailabilitySettings').then(m => ({
    default: m.AdminAvailabilitySettings,
  }))
);
const AdminStaffManagement = lazy(() =>
  import('./AdminStaffManagement').then(m => ({
    default: m.AdminStaffManagement,
  }))
);
const TenantSettings = lazy(() =>
  import('../TenantSettings').then(m => ({ default: m.TenantSettings }))
);
import { useHasAdminAccess } from '../../hooks/useCurrentUserRole';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useTenant } from '../../hooks/queries';
import {
  AdminTenantProvider,
  type AdminTenantContextValue,
} from '../../contexts/AdminTenantContext';
import { type TenantAnnouncement } from '../../api/tenantAnnouncementsClient';
import toast from 'react-hot-toast';

const API_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : 'http://localhost:8787';

/**
 * Admin 子路由類型
 * Note: 'coupons' route removed - may be restored in the future
 */
type AdminRoute =
  | 'users'
  | 'products'
  | 'orders'
  | 'preorders'
  | 'shipments'
  | 'appointments'
  | 'marketing'
  | 'survey-design'
  | 'rag-chatbot'
  | 'staff-management'
  | 'tenant-settings'
  | 'availability-settings';

/**
 * Admin 主要布局元件
 */
export function AdminLayout() {
  // 檢查用戶是否有後台訪問權限
  const { hasAccess, isLoading: isRoleLoading, role } = useHasAdminAccess();

  // 從 URL 參數獲取當前路由與「以管理員身份檢視的租戶 ID」（Super Admin 從租戶詳情點「商家設定」帶入）
  const getCurrentRouteFromURL = (): AdminRoute => {
    const urlParams = new URLSearchParams(window.location.search);
    const route = urlParams.get('tab') as AdminRoute;
    // Coupon route removed - 優惠券路由已移除
    return route &&
      [
        'users',
        'products',
        'orders',
        'preorders',
        'shipments',
        'appointments',
        'marketing',
        'survey-design',
        'rag-chatbot',
        'staff-management',
        'tenant-settings',
        'availability-settings',
      ].includes(route)
      ? route
      : 'users';
  };

  const [currentRoute, setCurrentRoute] = useState<AdminRoute>(() => {
    return getCurrentRouteFromURL();
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // 管理員檢視指定租戶時由 URL 帶入（?asTenantId=xxx）
  const asTenantId =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('asTenantId')
      : null;

  // 公告與 tenant 資料源共用：從 localStorage 讀取 tenant_id
  const tenantId =
    typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
  // 單一 tenant 資料源：Admin 內只呼叫一次 useTenant，子元件從 Context 讀取
  const effectiveTenantId = asTenantId || tenantId;
  const adminFetchOptions = useMemo(() => {
    if (!effectiveTenantId) return undefined;
    const token =
      localStorage.getItem('tenant_token') ||
      localStorage.getItem('staff_session_token');
    if (!token) return undefined;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': effectiveTenantId,
      },
    } as RequestInit;
  }, [effectiveTenantId]);
  const {
    tenant: adminTenant,
    isLoading: isAdminTenantLoading,
    error: adminTenantError,
    mutate: mutateAdminTenant,
  } = useTenant(
    effectiveTenantId && adminFetchOptions
      ? {
          fetch: adminFetchOptions,
          enabled: !!effectiveTenantId,
          revalidateOnFocus: false,
          dedupingInterval: 10000,
        }
      : { enabled: false }
  );
  const adminTenantContextValue = useMemo(
    () => ({
      tenant: adminTenant,
      isLoading: isAdminTenantLoading,
      error: adminTenantError,
      mutate: mutateAdminTenant,
      effectiveTenantId,
    }),
    [
      adminTenant,
      isAdminTenantLoading,
      adminTenantError,
      mutateAdminTenant,
      effectiveTenantId,
    ]
  );

  // 公告：使用 SWR 共用快取，30 秒內不重複打
  const announcementsKey =
    hasAccess && tenantId
      ? (['/api/tenants/announcements/active', tenantId] as const)
      : null;
  const { data: announcementsData } = useSWR<{
    success: boolean;
    data: TenantAnnouncement[];
  }>(
    announcementsKey,
    async ([path]) => {
      const token = localStorage.getItem('tenant_token');
      const tid = localStorage.getItem('tenant_id');
      if (!token || !tid) throw new Error('No auth');
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tid,
        },
      });
      if (!res.ok) {
        let errData: { message?: string } = {};
        try {
          const json = await res.json();
          if (json && typeof json === 'object') errData = json;
        } catch {
          errData = { message: `HTTP ${res.status}` };
        }
        const err = new Error(errData?.message ?? `HTTP ${res.status}`);
        Object.assign(err, { response: { data: errData } });
        throw err;
      }
      return res.json();
    },
    {
      dedupingInterval: 30000,
      revalidateOnFocus: false,
    }
  );
  const announcements =
    announcementsData?.success && Array.isArray(announcementsData?.data)
      ? announcementsData.data
      : [];

  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<
    Set<number>
  >(new Set());

  // 取得 Feature Flags
  const { isLoading: isFeatureFlagsLoading, flags } = useFeatureFlags(
    tenantId || undefined
  );

  // 如果用戶沒有後台訪問權限，重定向到 POS
  useEffect(() => {
    if (!isRoleLoading && !hasAccess) {
      toast.error('您沒有權限訪問後台管理頁面');
      window.location.href = '/pos';
    }
  }, [hasAccess, isRoleLoading]);

  /**
   * 更新當前路由並同步到 URL
   * 必須在 useEffect 之前定義，因為 useEffect 會使用它
   */
  const updateCurrentRoute = useCallback((route: AdminRoute) => {
    setCurrentRoute(route);
    // 更新 URL 參數
    const url = new URL(window.location.href);
    url.searchParams.set('tab', route);
    window.history.replaceState({}, '', url.toString());
  }, []);

  // 檢查 Feature Flags 並重定向到適當的路由（使用 useEffect 避免在渲染期間調用 setState）
  // 使用 useRef 追蹤是否已經重定向過，避免重複重定向
  const hasRedirectedRef = useRef<Record<string, boolean>>({});
  const flagsRef = useRef<string[]>([]);

  // 當 Feature Flags 載入完成時，更新 ref
  useEffect(() => {
    if (!isFeatureFlagsLoading && flags) {
      flagsRef.current = flags;
    }
  }, [isFeatureFlagsLoading, flags]);

  useEffect(() => {
    // 如果 Feature Flags 還在載入中，不進行檢查
    if (isFeatureFlagsLoading) {
      return;
    }

    // 檢查當前路由是否需要特定的 Feature Flag
    const routeFeatureFlagMap: Record<AdminRoute, string | null> = {
      users: null,
      products: null,
      orders: null,
      preorders: 'orders:preorder',
      shipments: 'orders:shipment',
      appointments: 'module:appointment',
      marketing: null,
      'survey-design': null,
      'rag-chatbot': 'ai:rag_chatbot',
      'staff-management': null,
      'tenant-settings': null,
      'availability-settings': 'module:appointment',
    };

    const requiredFlag = routeFeatureFlagMap[currentRoute];
    if (requiredFlag) {
      // 使用 ref 中的 flags 來檢查，避免依賴 isEnabled 函數
      const isFlagEnabled = flagsRef.current.includes(requiredFlag);
      if (!isFlagEnabled) {
        // 只有在路由確實需要 Feature Flag 且未啟用時才重定向
        // 避免重複重定向（檢查是否已經是 'users' 或已經重定向過）
        if (
          currentRoute !== 'users' &&
          !hasRedirectedRef.current[currentRoute]
        ) {
          hasRedirectedRef.current[currentRoute] = true;
          toast.error('此功能已停用');
          // 直接更新狀態和 URL，避免觸發額外的渲染
          setCurrentRoute('users');
          const url = new URL(window.location.href);
          url.searchParams.set('tab', 'users');
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        // 如果功能已啟用，清除重定向標記
        hasRedirectedRef.current[currentRoute] = false;
      }
    }
    // 不將 isEnabled 和 updateCurrentRoute 作為依賴項（它們會導致無限循環）
  }, [currentRoute, isFeatureFlagsLoading]);

  // 設定頁僅 OWNER 可存取；管理者（MANAGER）從 URL 進入時導向用戶管理
  useEffect(() => {
    if (isRoleLoading || currentRoute !== 'tenant-settings') return;
    if (role !== 'OWNER') {
      updateCurrentRoute('users');
    }
  }, [isRoleLoading, role, currentRoute, updateCurrentRoute]);

  // 如果正在載入角色或沒有權限，顯示載入中或重定向
  if (isRoleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null; // 將重定向到 POS，這裡返回 null
  }

  /**
   * 渲染當前子路由
   * 注意：不再在渲染期間檢查 Feature Flags 和調用 setState，這會導致無限循環
   * Feature Flags 檢查已移至 useEffect
   */
  const renderAdminRoute = () => {
    switch (currentRoute) {
      case 'users':
        return <AdminUsers />;
      case 'products':
        return <AdminProducts />;
      case 'preorders':
        return <AdminPreorders />;
      // Coupon route hidden - 優惠券路由已隱藏 (2024-11-11)
      // case 'coupons':
      //   return <AdminCoupons />;
      case 'orders':
        return <AdminOrders />;
      case 'shipments':
        return <AdminShipments />;
      case 'appointments':
        return <AdminAppointments />;
      case 'marketing':
        // 檢查進階促銷功能是否啟用（僅 BOGO 規則需要，但整個頁面都檢查以簡化邏輯）
        // 注意：簡單折扣（PERCENTAGE, FIXED_AMOUNT）不需要 Feature Flag
        // 但為了統一管理，我們在路由層級檢查
        // 實際的後端檢查只在建立/更新 BOGO 規則時進行
        return <AdminMarketing />;
      case 'survey-design':
        return <AdminSurveyDesign />;
      case 'rag-chatbot':
        return <AdminRagChatbot />;
      case 'staff-management':
        return <AdminStaffManagement />;
      case 'tenant-settings':
        return (
          <TenantSettings
            isAdmin={true}
            adminViewingTenantId={asTenantId || undefined}
          />
        );
      case 'availability-settings':
        return <AdminAvailabilitySettings />;
      default:
        return <AdminUsers />;
    }
  };

  // 過濾出未關閉的公告
  const visibleAnnouncements = announcements.filter(
    announcement => !dismissedAnnouncementIds.has(announcement.id)
  );

  // 關閉公告
  const handleDismissAnnouncement = (id: number) => {
    setDismissedAnnouncementIds(prev => new Set([...prev, id]));
  };

  return (
    <AdminTenantProvider
      value={adminTenantContextValue as AdminTenantContextValue}
    >
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="flex">
          {/* 左側導航 */}
          <AdminSidebar
            currentRoute={currentRoute}
            onRouteChange={(route: string) =>
              updateCurrentRoute(route as AdminRoute)
            }
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />

          {/* 右側主內容區 */}
          <div
            className={clsx(
              'flex-1 transition-all duration-300',
              isSidebarCollapsed ? 'ml-16' : 'ml-64'
            )}
          >
            {/* 公告 Banner */}
            {visibleAnnouncements.length > 0 && (
              <div className="p-4 space-y-2">
                {visibleAnnouncements.map(announcement => (
                  <div
                    key={announcement.id}
                    className="bg-blue-100 border-2 border-blue-400 rounded-lg p-4 shadow-md"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-blue-900">
                            公告
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {announcement.content}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          handleDismissAnnouncement(announcement.id)
                        }
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors text-xl font-bold leading-none"
                        aria-label="關閉公告"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="p-6">
              <Suspense
                fallback={
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="animate-pulse">
                      <div className="h-12 bg-gray-200" />
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-16 bg-gray-100 border-t border-gray-200"
                        />
                      ))}
                    </div>
                  </div>
                }
              >
                {renderAdminRoute()}
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </AdminTenantProvider>
  );
}
